// noinspection ExceptionCaughtLocallyJS
// noinspection JSIgnoredPromiseFromCall

import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { BehaviorSubject, catchError, filter, firstValueFrom, throwError, } from 'rxjs';
import { Claim, LogoutResponse } from '@web/csl-lib-models';
import { createClaimsTable } from '@web/csl-lib-utils';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { SERVICE_CONFIG_TOKEN, ServiceConfig } from '@web/csl-lib-services';


@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);


  /* Member Vars */

  private serviceConfig: ServiceConfig;
  private http!: HttpClient;
  private readonly apiUrl! : string;

  public loginDisplaySig = signal(false);

  // Use BehaviorSubject to provide an observable for claims availability
  private claimsSubject = new BehaviorSubject<Claim[]>([]);
  claims$ = this.claimsSubject.asObservable();

  loginDisplay = false;

  /* Lifecycle Hooks */

  constructor() {
    this.http = inject(HttpClient);
    this.serviceConfig = inject(SERVICE_CONFIG_TOKEN);
    this.apiUrl = this.serviceConfig.apiUrl;

    this.getClaims(undefined);
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        const account = result.payload as AccountInfo;
        this.msalService.instance.setActiveAccount(account);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        const claims = this.msalService.instance.getActiveAccount()?.idTokenClaims;
        this.getClaims(claims);
      });
  }


  /* Instance Methods */

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  getClaims(claims: any) {
    if (claims) {
      const claimsTable = createClaimsTable(claims);
      this.claimsSubject.next([...claimsTable]);
    } else {
      this.claimsSubject.next([]); // No claims available
    }
  }

  // Logout with backend token blocking
  async logoutWithTokenBlock(logoutFromAllDevices: boolean = false): Promise<void> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        throw new Error('No active account found');
      }

      // Call backend to block the token(s)
      const endpoint = logoutFromAllDevices ? '/api/auth/logout-all' : '/api/auth/logout';

      try {
        const response = await firstValueFrom(
          this.http.post<LogoutResponse>(`${this.apiUrl}${endpoint}`, {})
            .pipe(
              catchError((error: HttpErrorResponse) => {
                console.warn('Backend logout call failed:', error);
                // Continue with MSAL logout even if backend call fails
                return throwError(() => error);
              })
            )
        );
        console.log('Backend logout successful:', response.message);
      } catch (error) {
        console.warn('Backend logout failed, continuing with MSAL logout:', error);
      }

      // Clear session storage because Clinic state is persisted there
      sessionStorage.clear();

      // Perform MSAL logout (this will redirect)
      // Note: MSAL does NOT derive idTokenHint from `account` automatically - it must be
      // passed explicitly, using the raw ID token cached on the account, for B2C to validate
      // postLogoutRedirectUri against the app's registered redirect URIs.
      const logoutRequest = {
        account: account,
        idTokenHint: account.idToken,
        postLogoutRedirectUri: this.serviceConfig.logoutRedirectUrl
      };

      this.msalService.logoutRedirect(logoutRequest);

    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }


  // Quick logout for blocked tokens (no backend call needed since token is already blocked)
  // (not currently used, but can be uncommented if needed)
  /*
  handleBlockedTokenLogout(): void {
    console.warn('Handling blocked token logout');

    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      const logoutRequest = {
        account: account,
        postLogoutRedirectUri: this.serviceConfig.logoutRedirectUrl
      };

      this.msalService.logoutRedirect(logoutRequest);
    } else {
      // If no active account, just clear cache and redirect
      this.msalService.instance.clearCache().then(() => {});
      window.location.href = this.serviceConfig.logoutRedirectUrl;
    }
  }
  */

}
